// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * EmblaCarousel — a thin React wrapper around Embla that adds:
 *
 *  - declarative props for the common controls (arrows, pagination, scrollbar)
 *  - responsive `slidesPerView` configuration
 *  - optional 2D grid layout (`subGridRows`)
 *  - optional autoplay and auto-height plugins
 *  - inline ("overlay") or outer ("below") controls placement
 *
 * The component is intentionally lightweight: rendering of each subcomponent
 * is delegated, and Embla state is fanned out through `CarouselContext`.
 *
 * @example
 *   <EmblaCarousel slidesPerView={3} spaceBetween={20} navigation pagination>
 *       {items.map(it => <Card key={it.id} {...it} />)}
 *   </EmblaCarousel>
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowNavigation from "@stoneage/App/Components/EmblaCarousel/ArrowNavigation";
import { CarouselProvider } from "@stoneage/App/Components/EmblaCarousel/CarouselContext";
import CarouselControls from "@stoneage/App/Components/EmblaCarousel/CarouselControls";
import { useCarousel } from "@stoneage/App/Components/EmblaCarousel/hooks";
import { XngCarouselProps } from "@stoneage/App/Components/EmblaCarousel/models";
import {
    CarouselContainer,
    CarouselLayoutProps,
    CarouselWrapper,
    SlideItem,
    SlidesContainer,
} from "@stoneage/App/Components/EmblaCarousel/styled";
import { xCarouselStyle } from "@stoneage/App/Components/EmblaCarousel/styles";
import { mergeWithCss } from "@stoneage/XDL/Utilities/mergeWithCss";
import { EmblaOptionsType, EmblaPluginType } from "embla-carousel";
import AutoHeight from "embla-carousel-auto-height";
import Autoplay from "embla-carousel-autoplay";
import React, { FC, useEffect, useMemo, useState } from "react";
import { css } from "styled-components";

export interface CarouselStyle {
    container?: InjectableCss;
}

const DEFAULT_AUTOPLAY_DELAY_MS = 4000;
const DEFAULT_SPACE_BETWEEN_PX = 20;

/**
 * Default styles for the carousel wrapper, layered on top of `xCarouselStyle`.
 *
 * Includes the arrow-button rules and the vertical-mode tweaks (which depend
 * on runtime props like `navigationOutside`, `direction`, and `height`).
 */
export const carouselStyles: CarouselStyle = {
    container: {
        css: css`
            ${xCarouselStyle}
        `,
    },
};

const EmblaCarousel: FC<XngCarouselProps> = ({
    children,
    className = "",
    extendedStyles,
    height = "480px",
    navigation = true,
    spaceBetween = DEFAULT_SPACE_BETWEEN_PX,
    navigationOutside = false,
    pagination = false,
    scrollbar = true,
    updateOnWindowResize = true,
    onInit,
    subGridRows,
    slidesPerView,
    autoHeight = false,
    autoplay = false,
    loop = false,
    options: optionsProp,
    inlineNavigation = false,
    showControls = true,
    direction = "horizontal",
    pausable = false,
    onPreviousNavigationClick,
    onNextNavigationClick,
}) => {
    const [styles] = useState(() => mergeWithCss(carouselStyles, extendedStyles));

    const plugins: EmblaPluginType[] = useMemo(() => {
        const list: EmblaPluginType[] = [];
        if (autoHeight) list.push(AutoHeight());
        if (autoplay) {
            list.push(Autoplay(typeof autoplay === "object" ? autoplay : { delay: DEFAULT_AUTOPLAY_DELAY_MS }));
        }
        return list;
    }, [autoHeight, autoplay]);

    const options: EmblaOptionsType = useMemo(
        () => ({
            loop,
            axis: direction === "vertical" ? "y" : "x",
            // Drag freely unless the carousel shows exactly one slide at a time
            // (in which case we want a clean snap-per-page interaction).
            dragFree: slidesPerView !== 1,
            align: "start",
            ...optionsProp,
        }),
        [loop, direction, slidesPerView, optionsProp],
    );

    const { carouselRef, carouselApi, contextValue } = useCarousel(options, plugins);
    const { canScroll } = contextValue;

    const paginationOptions = pagination
        ? typeof pagination === "boolean"
            ? { clickable: true }
            : pagination
        : undefined;

    const scrollbarOptions = scrollbar ? (typeof scrollbar === "boolean" ? { draggable: true } : scrollbar) : undefined;

    const showPagination = Boolean(paginationOptions);
    const showScrollbar = Boolean(scrollbarOptions);
    const isPaginationClickable = paginationOptions?.clickable ?? true;
    const isScrollbarDraggable = scrollbarOptions?.draggable ?? false;

    const shouldHideControls = !canScroll || React.Children.count(children) <= 1;
    const areControlsVisible = showControls && !shouldHideControls && (navigation || showPagination || showScrollbar);

    const layoutProps: CarouselLayoutProps = {
        rows: subGridRows,
        view: subGridRows ? "grid" : "flex",
        loop: options.loop,
        slidesPerView,
        spaceBetween,
        vertical: direction === "vertical",
        height,
    };

    // Re-init Embla when children count changes (e.g. product removed from compare)
    useEffect(() => {
        if (!carouselApi) {
            return;
        }
        const id = setTimeout(() => carouselApi.reInit(), 0);
        return () => clearTimeout(id);
    }, [React.Children.count(children), carouselApi]);

    // Re-init Embla when the window resizes.
    useEffect(() => {
        if (!updateOnWindowResize || !carouselApi) return;

        const handleResize = () => carouselApi.reInit();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [carouselApi, updateOnWindowResize]);

    useEffect(() => {
        if (carouselApi && onInit) onInit(carouselApi, carouselRef);
    }, [carouselApi, onInit, carouselRef]);

    const renderControls = (inline: boolean) => (
        <CarouselControls
            showNavigation={navigation}
            showPagination={showPagination}
            paginationClickable={isPaginationClickable}
            showScrollbar={showScrollbar}
            scrollbarDraggable={isScrollbarDraggable}
            // Inline controls drive a freer thumb because they're paired with
            // continuous (often `dragFree`) interaction.
            scrollbarMode={inline ? "percentage" : "stepper"}
            inline={inline}
            navigationOutside={navigationOutside || direction === "vertical"}
            pausable={pausable}
        />
    );

    const renderSlide = (slide: React.ReactNode, key: number) => {
        if (subGridRows) {
            return <React.Fragment key={key}>{slide}</React.Fragment>;
        }
        return (
            <SlideItem key={key} {...layoutProps} inlineNavigation={inlineNavigation}>
                {slide}
                {areControlsVisible && inlineNavigation && renderControls(true)}
            </SlideItem>
        );
    };

    return (
        <CarouselProvider value={contextValue}>
            <StyledWrapper
                {...styles.container}
                className={`carousel-main-wrapper ${className}`}
                role="region"
                aria-label="Carousel"
            >
                <CarouselWrapper>
                    {areControlsVisible && (navigationOutside || direction === "vertical") && (
                        <ArrowNavigation
                            arrowType="prev"
                            showOnMobile={false}
                            navigationOutside={navigationOutside}
                            onClick={onPreviousNavigationClick}
                        />
                    )}
                    <CarouselContainer ref={carouselRef} aria-roledescription="carousel" {...layoutProps}>
                        <SlidesContainer {...layoutProps}>{React.Children.map(children, renderSlide)}</SlidesContainer>
                    </CarouselContainer>
                    {areControlsVisible && (navigationOutside || direction === "vertical") && (
                        <ArrowNavigation
                            arrowType="next"
                            showOnMobile={false}
                            navigationOutside={navigationOutside}
                            onClick={onNextNavigationClick}
                        />
                    )}
                </CarouselWrapper>

                {areControlsVisible && !inlineNavigation && renderControls(false)}
            </StyledWrapper>
        </CarouselProvider>
    );
};

export default EmblaCarousel;
